import { useEffect } from "react"

import { useViewerStore } from "Pods/ThreeDStudio/Hooks/useViewerStore"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"

import { NOOP } from "Utils/stableDefaults"

import useViewerControls from "./useViewerControls"

const RENDER_MODE_CYCLE = ["clay", "basecolor", "roughness", "normals", "metalness", "segmentation"]

function useViewerHotkeys({ onToggleEnvironmentSettings = NOOP } = {}) {
  const viewer = useViewerContext()
  const models = useViewerStore(viewer, "models")
  const wireframe = useViewerStore(viewer, "wireframe")
  const renderMode = useViewerStore(viewer, "renderMode")
  const { toggleGrid, toggleAutoRotate, resetCamera } = useViewerControls()

  const hasLoadedModel = Boolean(models?.loadedModels?.length && !models?.isLoading)
  const isWireframeAvailable = wireframe !== null
  const isPbrAvailable = Boolean(renderMode?.availableModes?.has("pbr"))

  useEffect(() => {
    if (!hasLoadedModel) {
      return
    }

    function handleKeyDown(event) {
      // Leave browser/OS combos (Cmd+W, Ctrl+R, ...) untouched.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      const tag = document.activeElement?.tagName
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        document.activeElement?.isContentEditable
      ) {
        return
      }

      const key = event.key?.toLowerCase()

      if (key === "w" && isWireframeAvailable) {
        viewer?.setWireframeSettings({ visible: !wireframe?.visible })
      } else if (key === "m" && isPbrAvailable) {
        viewer?.setRenderModeSettings({ mode: "pbr" })
      } else if (key === "c") {
        const cycleModes = RENDER_MODE_CYCLE.filter(mode => renderMode?.availableModes?.has(mode))
        if (cycleModes.length === 0) {
          return
        }

        const currentIndex = cycleModes.indexOf(renderMode?.mode)
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % cycleModes.length
        viewer?.setRenderModeSettings({ mode: cycleModes[nextIndex] })
      } else if (key === "g") {
        toggleGrid()
      } else if (key === "r") {
        toggleAutoRotate()
      } else if (key === "f") {
        resetCamera()
      } else if (key === "l") {
        onToggleEnvironmentSettings()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    viewer,
    hasLoadedModel,
    wireframe?.visible,
    isWireframeAvailable,
    isPbrAvailable,
    renderMode,
    toggleGrid,
    toggleAutoRotate,
    resetCamera,
    onToggleEnvironmentSettings,
  ])
}

export default useViewerHotkeys
